<script setup lang="ts" generic="T extends HasId">
import { ComboboxAnchor, ComboboxCancel, ComboboxViewport } from 'radix-vue'
import { computed, ref } from 'vue'

import { Loader } from '@/shared'
import { HasId } from '@/types'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, Icon } from '@/ui'
import { cn } from '@/utils'

const props = defineProps<{
  modelValue?: string
  searchTerm?: string
  options: T[]
  optionLabel?: string
  classNames?: string
  inputWrapperClassNames?: string
  inputClassNames?: string
  placeholder?: string
  isLoading?: boolean
  disabled?: boolean
  resetSearchTermOnBlur?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: string): void
  (e: 'update:searchTerm', val: string): void
}>()

const open = ref(false)

const value = computed({
  get: () => props.modelValue,
  set: (val: string) => emit('update:modelValue', val),
})

const searchTerm = computed({
  get: () => props.searchTerm || '',
  set: (val: string = '') => {
    emit('update:searchTerm', val)
  },
})
</script>

<template>
  <Command
    v-model="value"
    v-model:search-term="searchTerm"
    v-model:open="open"
    :default-open="false"
    :filter-function="res => res"
    :reset-search-term-on-blur="resetSearchTermOnBlur"
    :class="cn('relative min-w-[240px] overflow-visible rounded-full', classNames)"
  >
    <ComboboxAnchor>
      <CommandInput
        :auto-focus="false"
        :placeholder="placeholder || 'Type to search...'"
        :disabled="disabled"
        :class="inputClassNames"
        :wrapper-class="
          cn('pl-4 pr-8 border border-input bg-background hover:bg-secondary rounded-full', inputWrapperClassNames)
        "
        icon-class="size-5"
        @focus="open = true"
      />
      <ComboboxCancel v-if="searchTerm.length && !disabled" class="absolute top-1/2 right-3 -translate-y-1/2 transform">
        <Icon name="XMarkIcon" class="size-4" />
      </ComboboxCancel>
    </ComboboxAnchor>
    <CommandList
      v-show="searchTerm.length > 2"
      dismissable
      class="bg-popover absolute top-[50px] z-20 mt-1.5 w-max min-w-[240px] overflow-hidden rounded-md border shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]"
    >
      <ComboboxViewport class="relative max-h-[300px] min-h-8">
        <Loader :is-loading="isLoading" absolute loader-class-names="size-5" />
        <CommandEmpty v-if="!isLoading" class="py-2 text-center text-xs font-medium">
          <span v-if="!$slots['empty']">No results found.</span>
          <slot v-else name="empty" />
        </CommandEmpty>

        <CommandGroup :heading="optionLabel">
          <CommandItem
            v-for="(option, index) in options"
            :key="index"
            :value="option.id as string"
            class="whitespace-nowrap"
          >
            <slot v-if="$slots['default']" :option="option" />
            <span v-else>{{ option['name'] }}</span>
          </CommandItem>
        </CommandGroup>
      </ComboboxViewport>
    </CommandList>
  </Command>
</template>
